import React, { useState, useRef, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Checkbox from './Checkbox'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import AppTag from '../../../../../fragments/AppTag'
import AiConversationTag from './AiConversationTag'
import { getConversationTagLabel, isConversationTagInboxTab } from '../../../../../stores/inbox/inboxConversationTagDefaults'
import SnoozeMenu from './SnoozeMenu'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import { ITag } from '../../../../../interfaces/models/connection'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { shouldShowSnoozedReturnedLabel } from '../snooze/snoozedReturnedLabel'
import { useIsSharedInbox } from '../../../../../stores/inbox/InboxModeContext'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'

interface IConversationItemProps {
    conversation: any
    onOpen: (conversation: any) => void
    onToggleSelect: (conversation: any) => void
    onVisitLinkedIn: (conversationId: string) => void
    onSnooze: (conversationId: string, snoozedUntil: number | null) => void
}

const INBOX_TAGS_MAX_VISIBLE = 4

type InboxTagListItem =
    | { type: 'ai'; key: string; label: string }
    | { type: 'user'; key: string; tag: ITag }

const ConversationItem: React.FC<IConversationItemProps> = ({ conversation, onOpen, onToggleSelect, onVisitLinkedIn, onSnooze }) => {
    const { store: { selectedConversation, activeInboxLabel, selectedTab } } = useInboxStore()
    const { store: { user } } = useAuthStore()
    const isSharedInbox = useIsSharedInbox()
    const [isHovered, setIsHovered] = useState(false)
    const [snoozeAnchor, setSnoozeAnchor] = useState<{ top: number; right: number } | null>(null)
    const snoozeButtonRef = useRef<HTMLButtonElement>(null)

    const isSnoozed = !!(conversation.snoozedUntil && conversation.snoozedUntil > Date.now())
    const showSnoozedReturnedLabel = shouldShowSnoozedReturnedLabel(conversation)

    const isSalesNav =
        selectedTab === 'SalesNavigator' ||
        activeInboxLabel === 'salesNav' ||
        !!conversation.isSales

    const showAiConversationTagChip =
        isSalesNav ||
        activeInboxLabel === 'mainInbox' ||
        activeInboxLabel === 'allMessages' ||
        isConversationTagInboxTab(activeInboxLabel)
    const aiConversationTagLabel = getConversationTagLabel(conversation?.conversationTags)
    const conversationTagLabels = useMemo(
        () => [
            ...(conversation?.isSales ? ['SalesNav'] : []),
            ...(aiConversationTagLabel ? [aiConversationTagLabel] : []),
        ],
        [conversation?.isSales, aiConversationTagLabel]
    )
    const userTags = useMemo(
        () => conversation?.tags?.filter((t: { isForbidden?: boolean }) => !t.isForbidden) ?? [],
        [conversation?.tags]
    )
    const showTagsRow =
        (showAiConversationTagChip && conversationTagLabels.length > 0) || userTags.length > 0

    const [tagsExpanded, setTagsExpanded] = useState(false)

    useEffect(() => {
        setTagsExpanded(false)
    }, [conversation.conversationId])

    const allTagItems = useMemo<InboxTagListItem[]>(() => {
        const items: InboxTagListItem[] = []
        if (showAiConversationTagChip) {
            conversationTagLabels.forEach((label) => {
                items.push({ type: 'ai', key: `ai-${label}`, label })
            })
        }
        userTags.forEach((tag: ITag) => {
            items.push({ type: 'user', key: String(tag._id), tag })
        })
        return items
    }, [showAiConversationTagChip, conversationTagLabels, userTags])

    const hasMoreTags = allTagItems.length > INBOX_TAGS_MAX_VISIBLE
    const visibleTagItems =
        tagsExpanded || !hasMoreTags
            ? allTagItems
            : allTagItems.slice(0, INBOX_TAGS_MAX_VISIBLE)
    const hiddenTagsCount =
        hasMoreTags && !tagsExpanded ? allTagItems.length - INBOX_TAGS_MAX_VISIBLE : 0

    const formatSnoozeTime = (ts: number) => moment(ts).format('ddd h:mm A')

    const formatSnoozedReturnedTime = (ts: number) => {
        const minutesAgo = Math.max(0, moment().diff(moment(ts), 'minutes'))
        if (minutesAgo < 60) return '< 1 hour ago'

        const hoursAgo = Math.ceil(minutesAgo / 60)
        if (hoursAgo < 24) return `< ${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`

        return moment(ts).fromNow()
    }

    const getLastMessage = (conv: any) => {
        if (conv?.text) return conv.text
        if (conv?.attachments?.length || conv?.audioAttachments?.length) return 'Sent an attachment'
        if (conv?.gif) return 'Sent a GIF'
        if (conv?.forwardedMessage) return 'Forwarded a message'
        return ''
    }

    const formatName = (p: any) => {
        const name = [p?.firstName, p?.lastName].filter(Boolean).join(' ')
        return name || p?.publicIdentifier || 'Unknown'
    }

    const getDisplayName = (conv: any) => {
        if (conv?.participants?.length === 1) return formatName(conv.participants[0])
        if (conv?.conversationName) return conv.conversationName
        if (conv?.participants?.length === 2) return `${formatName(conv.participants[0])} and ${formatName(conv.participants[1])}`
        return `${formatName(conv?.participants?.[0])} and ${(conv?.participants?.length ?? 1) - 1} others`
    }

    const getTimestamp = (ts: any) => {
        const now = moment()
        const msgDate = moment(ts)
        if (now.diff(msgDate, 'days') < 1) return msgDate.fromNow()
        if (now.diff(msgDate, 'days') < 7) return msgDate.format('ddd')
        if (now.diff(msgDate, 'years') < 1) return msgDate.format('MMM D')
        return msgDate.format('MMM D, YYYY')
    }

    /**
     * Who sent the last message, as the list preview prefixes it. In the shared inbox "You"
     * is only right when you wrote it here; otherwise it is the teammate who did, or the
     * account itself (sent from the LinkedIn app or by a campaign).
     */
    const sentPrefix = (() => {
        if (!conversation?.isSent) return ''
        if (!isSharedInbox) return 'You: '
        const writer = conversation?.lastSentBy
        if (writer?._id && String(writer._id) === String(user?._id)) return 'You: '
        const name = writer?.firstName || String(conversation?.accountName || '').split(/\s+/)[0]
        return name ? `${name}: ` : 'You: '
    })()

    const isSelected = conversation.conversationId === selectedConversation?.conversationId

    const onCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onToggleSelect(conversation)
    }

    const onSnoozeClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (snoozeAnchor) {
            setSnoozeAnchor(null)
            return
        }
        const rect = snoozeButtonRef.current?.getBoundingClientRect()
        if (rect) {
            setSnoozeAnchor({ top: rect.top, right: window.innerWidth - rect.right })
        }
    }

    const handleSnooze = (snoozedUntil: number) => {
        onSnooze(conversation.conversationId, snoozedUntil)
        setSnoozeAnchor(null)
    }

    return (
        <Container
            selected={isSelected}
            salesNav={isSalesNav}
            onClick={() => !isSelected && onOpen(conversation)}
            data-conv-id={conversation.conversationId}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CheckboxSection>
                <Checkbox checkboxState={conversation?.isSelected} onClick={onCheckboxClick} />
            </CheckboxSection>
            <ProfilePhotoSection>
                {conversation?.participants?.length === 1 ? (
                    <StyledProfilePhoto
                        source={conversation?.participants?.[0]?.profilePicture}
                        capitalLetters={[conversation?.participants?.[0]?.firstName?.[0], conversation?.participants?.[0]?.lastName?.[0]].filter(Boolean).join('') || 'NN'}
                        allowTooltip={false}
                        width="40px"
                        height="40px"
                    />
                ) : (
                    <MultiplePeople>
                        <PhotoTopRight>
                            <SmallProfilePhoto
                                source={conversation?.participants?.[0]?.profilePicture}
                                capitalLetters={[conversation?.participants?.[0]?.firstName?.[0], conversation?.participants?.[0]?.lastName?.[0]].filter(Boolean).join('') || 'NN'}
                                allowTooltip={false}
                                width="24px"
                                height="24px"
                            />
                        </PhotoTopRight>
                        <PhotoBottomLeft>
                            <SmallProfilePhoto
                                source={conversation?.participants?.[1]?.profilePicture}
                                capitalLetters={[conversation?.participants?.[1]?.firstName?.[0], conversation?.participants?.[1]?.lastName?.[0]].filter(Boolean).join('') || 'NN'}
                                allowTooltip={false}
                                width="24px"
                                height="24px"
                            />
                        </PhotoBottomLeft>
                    </MultiplePeople>
                )}
            </ProfilePhotoSection>
            <TextSection faded={conversation?.isArchived}>
                <ConversationInfo>
                    <NameText bold snoozed={isSnoozed}>
                        {getDisplayName(conversation)}
                    </NameText>
                    <DatePinnedStarred>
                        {conversation?.isStarred && (
                            <svg width="16" height="16" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill="#FDBE00" fillRule="evenodd" d="m6.896 16.57 3.403-1.788 3.404 1.789a.833.833 0 0 0 1.208-.878l-.65-3.79 2.752-2.681a.834.834 0 0 0-.461-1.422l-3.805-.555-1.7-3.448a.834.834 0 0 0-1.495 0l-1.7 3.448-3.805.555a.834.834 0 0 0-.461 1.422l2.753 2.682-.651 3.789a.833.833 0 0 0 1.208.878z" />
                            </svg>
                        )}
                        {conversation?.isPinned && (
                            <svg width="12" height="12" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                                <path fill="#FF9100" fillRule="evenodd" d="m.837 14.845.533-1.598a.298.298 0 0 1 .06-.098l2.309-2.308L.72 7.822a.753.753 0 0 1 0-1.065c1.136-1.136 2.871-1.186 4.432-.616l2.896-2.353-.937-.937a.753.753 0 0 1 0-1.065L8.178.72a.753.753 0 0 1 1.065 0l6.036 6.036a.753.753 0 0 1 0 1.065l-1.065 1.066a.753.753 0 0 1-1.065 0l-.937-.937-2.353 2.896c.576 1.577.51 3.306-.616 4.432a.753.753 0 0 1-1.065 0l-3.019-3.018-2.308 2.308a.251.251 0 0 1-.098.061l-1.598.533a.251.251 0 0 1-.318-.318z" />
                            </svg>
                        )}
                        {isSnoozed ? (
                            <SnoozedTimeDisplay
                                onClick={(e) => { e.stopPropagation(); onSnooze(conversation.conversationId, null) }}
                            >
                                <span className="snooze-clock">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </span>
                                <span className="snooze-time">{formatSnoozeTime(conversation.snoozedUntil)}</span>
                                <span className="snooze-label">Unsnooze</span>
                            </SnoozedTimeDisplay>
                        ) : (
                            <DateOrSnoozeArea>
                                <DateText
                                    $hidden={isHovered || !!snoozeAnchor}
                                    // Shared threads open in the account owner's LinkedIn, not in this browser's.
                                    onClick={(e) => { e.stopPropagation(); if (!isSharedInbox) onVisitLinkedIn(conversation?.conversationId) }}
                                >
                                    {getTimestamp(conversation?.dateTimestamp)}
                                </DateText>

                                <SnoozeWrapper $visible={isHovered || !!snoozeAnchor}>
                                    <TooltipWrapper tooltipText="Snooze" placement="top">
                                        <SnoozeButton
                                            ref={snoozeButtonRef}
                                            onClick={onSnoozeClick}
                                            data-snooze-btn
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                        </SnoozeButton>
                                    </TooltipWrapper>
                                    {snoozeAnchor && (
                                        <SnoozeMenu
                                            anchor={snoozeAnchor}
                                            onSnooze={handleSnooze}
                                            onClose={() => setSnoozeAnchor(null)}
                                        />
                                    )}
                                </SnoozeWrapper>
                            </DateOrSnoozeArea>
                        )}
                    </DatePinnedStarred>
                </ConversationInfo>
                <LastMessageRow snoozed={isSnoozed}>
                    <LastMessageText bold={!conversation?.isRead} width={!conversation?.isRead}>
                        {sentPrefix}{getLastMessage(conversation)}
                    </LastMessageText>
                    {!conversation?.isRead && (
                        <svg width="10" height="10" viewBox="0 0 72 72">
                            <circle cx="36" cy="36" r="28" fill="#ff6660" />
                        </svg>
                    )}
                </LastMessageRow>
                {isSharedInbox && conversation?.accountName && (
                    <AccountRow>
                        <AccountChip title={`Conversation on ${conversation.accountName}'s LinkedIn`}>
                            {conversation.accountName}
                        </AccountChip>
                    </AccountRow>
                )}
                {showSnoozedReturnedLabel && (
                    <SnoozedReturnedInfo>
                        Snoozed {formatSnoozedReturnedTime(conversation.snoozedUntil)}
                    </SnoozedReturnedInfo>
                )}
                {showTagsRow && (
                    <TagsRow>
                        {visibleTagItems.map((item) =>
                            item.type === 'ai' ? (
                                <InboxTagWrap key={item.key}>
                                    <AiConversationTag label={item.label} />
                                </InboxTagWrap>
                            ) : (
                                <InboxTagWrap key={item.key}>
                                    <AppTag className="inbox-conversation-tag" tag={item.tag} />
                                </InboxTagWrap>
                            )
                        )}
                        {hiddenTagsCount > 0 && (
                            <MoreToggle
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setTagsExpanded(true)
                                }}
                            >
                                +{hiddenTagsCount} more
                            </MoreToggle>
                        )}
                        {hasMoreTags && tagsExpanded && (
                            <MoreToggle
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setTagsExpanded(false)
                                }}
                            >
                                Show less
                            </MoreToggle>
                        )}
                    </TagsRow>
                )}
            </TextSection>
        </Container>
    )
}

const Container = styled.div<{ selected: boolean; snoozed?: boolean; salesNav?: boolean }>`
    box-sizing: border-box;
    font-family: 'Nunito Sans', sans-serif;
    width: ${props => (props.selected ? 'calc(100% + 24px)' : '100%')};
    margin-left: ${props => (props.selected ? '-12px' : '0')};
    margin-right: ${props => (props.selected ? '-12px' : '0')};
    min-height: 64px;
    border-radius: 8px;
    border-left: none;
    background-color: ${props => {
        if (props.salesNav && props.selected) return '#0da2e728'
        if (props.salesNav) return '#0da2e714'
        if (props.selected) return 'rgba(100, 103, 242, 0.08)'
        return 'transparent'
    }};
    padding: ${props => (props.selected ? '10px 24px' : '10px 12px')};
    margin-bottom: 4px;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    flex-shrink: 0;

    &:hover {
        background-color: ${props => {
            if (props.salesNav && props.selected) return '#0da2e732'
            if (props.salesNav) return '#0da2e71f'
            if (props.selected) return 'rgba(100, 103, 242, 0.12)'
            return '#f1f5f9'
        }};
    }
`
const CheckboxSection = styled.div`
    width: 24px;
    display: flex;
    align-items: center;
    padding-top: 10px;
    flex-shrink: 0;
`
const ProfilePhotoSection = styled.div`
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    margin-right: 10px;
    position: relative;
`
const StyledProfilePhoto = styled(ProfilePhoto).attrs({ className: 'inbox-conversation-avatar' })`
    border-radius: 50%;
`
const MultiplePeople = styled.div`
    width: 40px;
    height: 40px;
    position: relative;
`
const SmallProfilePhoto = styled(ProfilePhoto).attrs({ className: 'inbox-conversation-avatar' })`
    border-radius: 50%;
    border: 1px solid white;
`
const PhotoTopRight = styled.div`
    position: absolute;
    top: 0;
    right: 0;
`
const PhotoBottomLeft = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
`
const TextSection = styled.div<{ faded?: boolean; snoozed?: boolean }>`
    flex: 1;
    min-width: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    opacity: ${props => props.faded ? 0.78 : 1};
`
const ConversationInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 20px;
    margin-bottom: 4px;
`
const NameText = styled.p<{ bold?: boolean; snoozed?: boolean }>`
    font-size: 14px;
    font-weight: 700;
    color: ${props => props.snoozed ? '#a8b0c4' : '#333951'};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin: 0;
    flex: 1;
    min-width: 0;
    line-height: 21px;
`
const DatePinnedStarred = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
    margin-left: 8px;
`
const DateOrSnoozeArea = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
`
const SnoozeWrapper = styled.div<{ $visible?: boolean }>`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    opacity: ${props => props.$visible ? 1 : 0};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};
`
const SnoozeButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 2px;
    border-radius: 4px;
    cursor: pointer;
    color: #8c96ad;
    line-height: 0;

    &:hover {
        color: #6467f2;
        background-color: rgba(100, 103, 242, 0.08);
    }
`
const SnoozedTimeDisplay = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    color: #e07b00;
    white-space: nowrap;
    cursor: pointer;
    line-height: 18px;
    border-radius: 4px;
    padding: 1px 3px;
    transition: background 0.12s, color 0.12s;

    .snooze-clock { display: inline-flex; align-items: center; flex-shrink: 0; }
    .snooze-time { display: inline; }
    .snooze-label { display: none; }

    &:hover {
        background: rgba(224, 123, 0, 0.1);
        color: #c0392b;
        .snooze-time { display: none; }
        .snooze-label { display: inline; }
    }
`
const DateText = styled.span<{ $hidden?: boolean }>`
    font-size: 12px;
    color: #8c96ad;
    white-space: nowrap;
    cursor: pointer;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
    opacity: ${props => props.$hidden ? 0 : 1};
    pointer-events: ${props => props.$hidden ? 'none' : 'auto'};
    &:hover { color: #7a89fc; }
`
const LastMessageRow = styled.div<{ snoozed?: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: ${props => props.snoozed ? 0.45 : 1};
`
const LastMessageText = styled.p<{ bold?: boolean; width?: boolean }>`
    font-size: 13px;
    font-weight: ${props => props.bold ? '700' : '400'};
    color: #333951;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin: 0;
    flex: 1;
    min-width: 0;
    line-height: 19.5px;
`
const AccountRow = styled.div`
    display: flex;
    margin-top: 4px;
    min-width: 0;
`
const AccountChip = styled.span`
    max-width: 100%;
    padding: 1px 8px;
    border-radius: 9999px;
    background: #f0efff;
    color: #6467f2;
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const SnoozedReturnedInfo = styled.div`
    margin-top: 3px;
    font-size: 12px;
    font-weight: 700;
    color: #d97706;
    line-height: 16px;
    white-space: nowrap;
`
const TagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;
    width: 100%;
    min-width: 0;
    justify-content: flex-start;
    align-items: center;
    padding-bottom: 2px;
`

const InboxTagWrap = styled.div`
    max-width: 100%;
    min-width: 0;
    display: inline-flex;
`

const MoreToggle = styled.button`
    appearance: none;
    height: 20px;
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    border-radius: 12px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    color: #41495e;
    border: 1px solid #dae0e8;
    background: #ffffff;
    cursor: pointer;
    flex-shrink: 0;
    box-sizing: border-box;

    &:hover {
        opacity: 0.8;
    }
`

export default ConversationItem
